import { supabase } from '../lib/supabaseClient';
import type { SavedFloorPlanData, SavedPropertyInventoryData } from './fileUtils';
import { getRetrofitSurveyAddress } from './retrofitSurveyStorage';

// Helper function to get current authenticated user
const getCurrentUserId = async (): Promise<string | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
};

// Helper function to get current user email
const getCurrentUserEmail = async (): Promise<string | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.email || null;
};

/**
 * Sanitize email for use in file paths
 * Converts to lowercase, replaces @ and . with hyphens
 */
const sanitizeEmail = (email: string | null | undefined): string => {
  if (!email) return 'unknown-user';
  
  return email
    .toLowerCase()
    .trim()
    .replace(/@/g, '-at-')          // user@example.com → user-at-example.com
    .replace(/\./g, '-')             // example.com → example-com
    .replace(/[^a-z0-9-]/g, '-')    // Remove any other special chars
    .replace(/-+/g, '-')             // Replace multiple hyphens with single
    .replace(/^-+|-+$/g, '')         // Remove leading/trailing hyphens
    .substring(0, 50);                // Limit length
};

// Database table definitions for Supabase
export type PlanType = 'standard' | 'estate' | 'retrofit' | 'energy';

export interface FloorPlanRecord {
  id?: string;
  name: string;
  data: SavedFloorPlanData;
  plan_type?: PlanType;
  created_at?: string;
  updated_at?: string;
  user_id?: string;
  organization_id?: string | null;
}

export interface FloorPlanListItem {
  id: string;
  name: string;
  plan_type?: PlanType;
  created_at?: string;
  updated_at?: string;
  user_id?: string;
  organization_id?: string | null;
}

export interface InventoryRecord {
  id?: string;
  name: string;
  data: SavedPropertyInventoryData;
  created_at?: string;
  updated_at?: string;
  user_id?: string;
  organization_id?: string | null;
}

export type InventoryListItem = {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
  organization_id?: string | null;
};

export type RetrofitSurveyListItem = {
  id: string;
  name: string;
  address?: string;
  created_at: string;
  updated_at: string;
  user_id?: string;
  organization_id?: string | null;
};

// Floor Plan Operations
export const saveFloorPlanToSupabase = async (
  name: string,
  data: SavedFloorPlanData,
  existingId?: string,
  planType: PlanType = 'energy'
): Promise<string> => {
  try {
    // Strip .json extension from existingId if present (from old local storage IDs)
    const cleanExistingId = existingId ? existingId.replace(/\.json$/i, '') : undefined;
    
    // ✅ CRITICAL FIX: Prevent saving with auto-generated names (plan-timestamp format)
    // Only allow saving when user has provided a real address
    if (/^plan-\d+$/i.test(name)) {
      throw new Error('Please provide a property address before saving');
    }
    
    // ✅ Additional validation: Name must be at least 3 characters and not be a timestamp
    if (!name || name.trim().length < 3) {
      throw new Error('Floor plan name must be at least 3 characters');
    }
    
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Get user's organization ID - handle 406 errors gracefully
    let organizationId = null;
    try {
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();
      
      if (memberError && memberError.code !== 'PGRST116') {
        console.warn(`[SaveFloorPlan] Organization query warning:`, memberError);
      }
      
      organizationId = memberData?.organization_id || null;
    } catch (error) {
      console.warn(`[SaveFloorPlan] Failed to get organization, continuing without:`, error);
    }
    console.log(`[SaveFloorPlan] 🏢 Organization ID: ${organizationId || 'none'}`);
    
    // 🔧 CRITICAL FIX: Ensure plan_type is always explicitly set to satisfy database constraint
    const validPlanType: PlanType = planType || 'energy';
    
    const record: Partial<FloorPlanRecord> = {
      name: name.replace(/\.json$/i, ''),
      data,
      plan_type: validPlanType,
      user_id: userId,
      organization_id: organizationId,
      updated_at: new Date().toISOString(),
    };

    // Calculate and log file size
    const sizeInBytes = calculateDataSize(record);
    
    // Count walls across all floors
    const wallCount = data.floorPlans ? Object.values(data.floorPlans).reduce((sum, walls: any) => sum + (walls?.length || 0), 0) : 0;
    
    console.log(`📊 [FILE SIZE] Saving floor plan:`, {
      id: cleanExistingId || 'new',
      name: record.name,
      planType: planType,
      sizeBytes: sizeInBytes,
      sizeFormatted: formatFileSize(sizeInBytes),
      floors: data.floorPlans ? Object.keys(data.floorPlans).length : 0,
      walls: wallCount,
      elements: data.elements?.length || 0,
      action: cleanExistingId ? 'UPDATE' : 'INSERT'
    });
    
    // Extra log for estate plans
    if (planType === 'estate') {
      console.log(`🏢 [ESTATE PLAN] Saving with plan_type='estate':`, {
        id: cleanExistingId || 'new',
        name: record.name
      });
    }

    if (cleanExistingId) {
      // 🔧 CRITICAL FIX: Explicitly ensure plan_type is set for UPDATE operation
      if (!record.plan_type) {
        record.plan_type = validPlanType;
      }
      
      // First, try to update the existing record
      const { data: updatedData, error: updateError } = await supabase
        .from('floorplans')
        .update(record)
        .eq('id', cleanExistingId)
        .select('id')
        .single();

      if (!updateError) {
        // Update successful
        return cleanExistingId;
      } else if (updateError.code === 'PGRST116') {
        // Record not found, treat as new record (was a temporary ID)
        console.log('[Supabase] Record not found for update, creating new record');
      } else {
        // Other error, re-throw
        throw updateError;
      }
    }

    // Create new record (either no ID provided or ID doesn't exist in DB)
    record.created_at = new Date().toISOString();
    
    // 🔧 CRITICAL FIX: Explicitly ensure plan_type is set for INSERT operation
    if (!record.plan_type) {
      record.plan_type = validPlanType;
    }
    
    const { data: insertedData, error } = await supabase
      .from('floorplans')
      .insert(record)
      .select('id')
      .single();

    if (error) throw error;
    console.log('[Supabase] Created new floor plan record with ID:', insertedData.id);
    return insertedData.id;
  } catch (error) {
    console.error('Error saving floor plan to Supabase:', error);
    throw error;
  }
};

export const loadFloorPlanFromSupabase = async (id: string): Promise<SavedFloorPlanData | null> => {
  try {
    const { data, error } = await supabase
      .from('floorplans')
      .select('data, name, created_at, updated_at')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        console.warn(`Floor plan with ID ${id} not found`);
        return null;
      }
      throw error;
    }

    // Calculate and log file size
    if (data?.data) {
      const sizeInBytes = calculateDataSize(data.data);
      
      // Count walls across all floors
      const wallCount = data.data.floorPlans ? Object.values(data.data.floorPlans).reduce((sum, walls: any) => sum + (walls?.length || 0), 0) : 0;
      
      console.log(`📊 [FILE SIZE] Loaded floor plan:`, {
        id,
        name: data.name,
        sizeBytes: sizeInBytes,
        sizeFormatted: formatFileSize(sizeInBytes),
        floors: data.data.floorPlans ? Object.keys(data.data.floorPlans).length : 0,
        walls: wallCount,
        elements: data.data.elements?.length || 0,
        created: data.created_at,
        updated: data.updated_at
      });
    }

    return data?.data || null;
  } catch (error) {
    console.error('Error loading floor plan from Supabase:', error);
    throw error;
  }
};

export const getAllFloorPlansFromSupabase = async (): Promise<FloorPlanListItem[]> => {
  try {
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('[FloorPlans] User not authenticated, returning empty list');
      return [];
    }
    
    console.log(`[FloorPlans] 🔍 Current user ID: ${userId}`);
    
    // Add 2-second timeout for organization check
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        console.warn('[FloorPlans] Organization check timed out, loading own files only');
        resolve(null);
      }, 2000);
    });
    
    // Check if user is in an organization and their role (use maybeSingle to avoid errors)
    const orgCheckPromise = supabase
      .from('organization_members')
      .select('organization_id, role')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();
    
    const orgResult = await Promise.race([orgCheckPromise, timeoutPromise]);
    const memberData = orgResult && 'data' in orgResult ? orgResult.data : null;
    
    const isAdmin = memberData?.role === 'super_admin' || memberData?.role === 'admin';
    const organizationId = memberData?.organization_id;
    
    console.log(`[FloorPlans] 👤 User role: ${memberData?.role || 'no organization'}`);
    console.log(`[FloorPlans] 🏢 Organization ID: ${organizationId || 'none'}`);
    console.log(`[FloorPlans] 🔑 Is Admin: ${isAdmin}`);
    
    let query = supabase
      .from('floorplans')
      .select('id, name, plan_type, created_at, updated_at, user_id, organization_id');
    
    if (isAdmin && organizationId) {
      // Admins see ALL files from their organization
      console.log(`[FloorPlans] 📂 Loading all files from organization (admin mode)`);
      query = query.eq('organization_id', organizationId);
    } else {
      // Regular members see ONLY their own files
      console.log(`[FloorPlans] 📂 Loading only own files (member mode)`);
      query = query.eq('user_id', userId);
    }
    
    const { data, error } = await query.order('updated_at', { ascending: false });

    if (error) throw error;
    
    const ownFiles = data?.filter(f => f.user_id === userId).length || 0;
    const teamFiles = (data?.length || 0) - ownFiles;
    const uniqueOwners = new Set(data?.map(f => f.user_id) || []);
    
    console.log(`[FloorPlans] ✅ Loaded ${data?.length || 0} total files (${ownFiles} own, ${teamFiles} team)`);
    console.log(`[FloorPlans] 👥 Files from ${uniqueOwners.size} unique user(s)`);
    
    if (teamFiles > 0) {
      const teamUserIds = data?.filter(f => f.user_id !== userId).map(f => f.user_id) || [];
      console.log(`[FloorPlans] 🎯 Team file owners:`, [...new Set(teamUserIds)]);
    }
    
    return data || [];
  } catch (error) {
    console.error('Error fetching floor plans from Supabase:', error);
    throw error;
  }
};

export const deleteFloorPlanFromSupabase = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('floorplans')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting floor plan from Supabase:', error);
    throw error;
  }
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Helper function to count photos in inventory data
const countPhotosInInventory = (data: SavedPropertyInventoryData): number => {
  let count = 0;
  if (data.rooms) {
    data.rooms.forEach(room => {
      if (room.items) {
        room.items.forEach(item => {
          count += item.photos?.length || 0;
        });
      }
    });
  }
  return count;
};

// Helper function to calculate data size
const calculateDataSize = (data: any): number => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch (e) {
    return 0;
  }
};

// Helper function to optimize inventory data before saving
const optimizeInventoryData = (data: SavedPropertyInventoryData): SavedPropertyInventoryData => {
  // Remove any base64 data that might have slipped through
  const optimized = JSON.parse(JSON.stringify(data));
  
  if (optimized.rooms) {
    optimized.rooms = optimized.rooms.map((room: any) => ({
      ...room,
      items: room.items.map((item: any) => ({
        ...item,
        photos: item.photos ? item.photos
          .filter((photo: any) => {
            // Only keep photos that have been successfully uploaded
            // Skip photos with base64 data in URL (not yet uploaded)
            const hasBase64Url = photo.url && photo.url.startsWith('data:image');
            if (hasBase64Url) {
              console.warn(`[Supabase] Skipping photo ${photo.id} - still has base64 URL (not yet uploaded)`);
              return false;
            }
            return true;
          })
          .map((photo: any) => ({
            id: photo.id,
            fileName: photo.fileName,
            url: photo.url,
            caption: photo.caption,
            timestamp: photo.timestamp,
            error: photo.error,
            order: photo.order
            // Explicitly exclude localPath, uploading, and any base64 data
          })) : []
      }))
    }));
  }
  
  return optimized;
};

// Inventory Operations
export const saveInventoryToSupabase = async (
  name: string,
  data: SavedPropertyInventoryData,
  existingId?: string
): Promise<string> => {
  try {
    // Strip .json extension from existingId if present (from old local storage IDs)
    const cleanExistingId = existingId ? existingId.replace(/\.json$/i, '') : undefined;
    
    // Optimize data before saving
    const optimizedData = optimizeInventoryData(data);
    
    // Calculate data size and warn if too large
    const dataSize = calculateDataSize(optimizedData);
    const dataSizeMB = (dataSize / (1024 * 1024)).toFixed(2);
    
    const photoCount = countPhotosInInventory(optimizedData);
    console.log(`📊 [FILE SIZE] Saving inventory:`, {
      id: cleanExistingId || 'new',
      name,
      sizeBytes: dataSize,
      sizeFormatted: formatFileSize(dataSize),
      photoCount,
      rooms: optimizedData.rooms?.length || 0,
      action: cleanExistingId ? 'UPDATE' : 'INSERT'
    });
    
    // Detailed size breakdown for debugging large payloads
    if (dataSize > 5 * 1024 * 1024) {
      const breakdown = {
        reportType: calculateDataSize(optimizedData.reportType || '') / 1024,
        propertyDetails: calculateDataSize(optimizedData.propertyDetails || {}) / 1024,
        rooms: calculateDataSize(optimizedData.rooms || []) / 1024,
        certificates: calculateDataSize(optimizedData.certificates || {}) / 1024,
        meterReadings: calculateDataSize(optimizedData.meterReadings || {}) / 1024,
        keysData: calculateDataSize(optimizedData.keysData || {}) / 1024,
        signatures: calculateDataSize(optimizedData.signatures || {}) / 1024,
      };
      
      console.warn(`[Supabase] Data size breakdown (KB):`, breakdown);
      console.warn(`[Supabase] Total rooms: ${optimizedData.rooms?.length || 0}`);
      
      // Check if any photos have base64 data instead of URLs
      let base64PhotoCount = 0;
      let totalPhotoCount = 0;
      optimizedData.rooms?.forEach((room: any) => {
        room.items?.forEach((item: any) => {
          item.photos?.forEach((photo: any) => {
            totalPhotoCount++;
            if (photo.url && photo.url.startsWith('data:image')) {
              base64PhotoCount++;
              console.error(`[Supabase] Found base64 photo URL in room ${room.name}, item ${item.name}:`, photo.id);
            }
            if (photo.localPath && photo.localPath.startsWith('data:image')) {
              console.error(`[Supabase] Found base64 in localPath (should be stripped):`, photo.id);
            }
          });
        });
      });
      
      console.warn(`[Supabase] Photo stats: ${totalPhotoCount} total, ${base64PhotoCount} with base64 data`);
    }
    
    // Warn if data is very large (> 10MB) - may cause slower saves or timeouts
    if (dataSize > 10 * 1024 * 1024) {
      console.warn(`[Supabase] Very large data size detected: ${dataSizeMB}MB - this may take significantly longer to save and could potentially timeout`);
    } else if (dataSize > 5 * 1024 * 1024) {
      console.warn(`[Supabase] Large data size detected: ${dataSizeMB}MB - this may take longer to save`);
    }
    
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    // Get user's organization ID
    const { data: memberData } = await supabase
      .from('organization_members')
      .select('organization_id')
      .eq('user_id', userId)
      .eq('is_active', true)
      .single();
    
    const organizationId = memberData?.organization_id || null;
    console.log(`[SaveInventory] 🏢 Organization ID: ${organizationId || 'none'}`);
    
    const record: Partial<InventoryRecord> = {
      name: name.replace(/\.json$/i, ''),
      data: optimizedData,
      user_id: userId,
      organization_id: organizationId,
      updated_at: new Date().toISOString(),
    };

    if (cleanExistingId) {
      // First, try to update the existing record
      const { data: updatedData, error: updateError } = await supabase
        .from('inventories')
        .update(record)
        .eq('id', cleanExistingId)
        .select('id')
        .single();

      if (!updateError) {
        // Update successful
        console.log(`[Supabase] Updated inventory record: ${cleanExistingId} (${dataSizeMB}MB)`);
        return cleanExistingId;
      } else if (updateError.code === 'PGRST116') {
        // Record not found, treat as new record (was a temporary ID)
        console.log('[Supabase] Record not found for update, creating new record');
      } else {
        // Other error, re-throw
        throw updateError;
      }
    }

    // Create new record (either no ID provided or ID doesn't exist in DB)
    record.created_at = new Date().toISOString();
    const { data: insertedData, error } = await supabase
      .from('inventories')
      .insert(record)
      .select('id')
      .single();

    if (error) throw error;
    console.log(`[Supabase] Created new inventory record with ID: ${insertedData.id} (${dataSizeMB}MB)`);
    return insertedData.id;
  } catch (error) {
    console.error('Error saving inventory to Supabase:', error);
    throw error;
  }
};

export const loadInventoryFromSupabase = async (id: string): Promise<SavedPropertyInventoryData | null> => {
  try {
    const { data, error } = await supabase
      .from('inventories')
      .select('data, name, created_at, updated_at')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        console.warn(`Inventory with ID ${id} not found`);
        return null;
      }
      throw error;
    }

    // Calculate and log file size
    if (data?.data) {
      const sizeInBytes = calculateDataSize(data.data);
      const photoCount = countPhotosInInventory(data.data);
      console.log(`📊 [FILE SIZE] Loaded inventory:`, {
        id,
        name: data.name,
        sizeBytes: sizeInBytes,
        sizeFormatted: formatFileSize(sizeInBytes),
        photoCount,
        rooms: data.data.rooms?.length || 0,
        created: data.created_at,
        updated: data.updated_at
      });
    }

    return data?.data || null;
  } catch (error) {
    console.error('Error loading inventory from Supabase:', error);
    throw error;
  }
};

export const getAllInventoriesFromSupabase = async (): Promise<InventoryListItem[]> => {
  try {
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('[Inventories] User not authenticated, returning empty list');
      return [];
    }
    
    console.log(`[Inventories] 🔍 Current user ID: ${userId}`);
    
    // Add 2-second timeout for organization check
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        console.warn('[Inventories] Organization check timed out, loading own files only');
        resolve(null);
      }, 2000);
    });
    
    // Check if user is in an organization and their role (use maybeSingle to avoid errors)
    const orgCheckPromise = supabase
      .from('organization_members')
      .select('organization_id, role')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();
    
    const orgResult = await Promise.race([orgCheckPromise, timeoutPromise]);
    const memberData = orgResult && 'data' in orgResult ? orgResult.data : null;
    
    const isAdmin = memberData?.role === 'super_admin' || memberData?.role === 'admin';
    const organizationId = memberData?.organization_id;
    
    console.log(`[Inventories] 👤 User role: ${memberData?.role || 'no organization'}`);
    console.log(`[Inventories] 🏢 Organization ID: ${organizationId || 'none'}`);
    console.log(`[Inventories] 🔑 Is Admin: ${isAdmin}`);
    
    let query = supabase
      .from('inventories')
      .select('id, name, created_at, updated_at, user_id, organization_id');
    
    if (isAdmin && organizationId) {
      // Admins see ALL files from their organization
      console.log(`[Inventories] 📂 Loading all files from organization (admin mode)`);
      query = query.eq('organization_id', organizationId);
    } else {
      // Regular members see ONLY their own files
      console.log(`[Inventories] 📂 Loading only own files (member mode)`);
      query = query.eq('user_id', userId);
    }
    
    const { data, error } = await query.order('updated_at', { ascending: false });

    if (error) throw error;
    
    const ownFiles = data?.filter(f => f.user_id === userId).length || 0;
    const teamFiles = (data?.length || 0) - ownFiles;
    console.log(`[Inventories] ✅ Loaded ${data?.length || 0} total files (${ownFiles} own, ${teamFiles} team)`);
    
    return data || [];
  } catch (error) {
    console.error('Error fetching inventories from Supabase:', error);
    throw error;
  }
};

export const deleteInventoryFromSupabase = async (id: string): Promise<void> => {
  try {
    // Step 1: Get user email for path construction
    const userEmail = await getCurrentUserEmail();
    if (!userEmail) {
      throw new Error('User not authenticated');
    }
    const sanitizedEmail = sanitizeEmail(userEmail);
    
    // Step 2: Delete all photos from storage bucket
    // Path pattern: <email>/<inventory-id>/...
    const storagePath = `${sanitizedEmail}/${id}`;
    
    console.log(`[Delete Inventory] Deleting storage files at: ${storagePath}`);
    
    try {
      // List all files in the inventory folder
      const { data: files, error: listError } = await supabase.storage
        .from('inventory-photos')
        .list(storagePath, {
          limit: 1000,
          offset: 0,
        });
      
      if (listError) {
        console.warn(`[Delete Inventory] Error listing files for ${id}:`, listError);
      } else if (files && files.length > 0) {
        // Delete all files
        const filePaths = files.map(file => `${storagePath}/${file.name}`);
        const { error: deleteError } = await supabase.storage
          .from('inventory-photos')
          .remove(filePaths);
        
        if (deleteError) {
          console.warn(`[Delete Inventory] Error deleting storage files:`, deleteError);
        } else {
          console.log(`[Delete Inventory] ✅ Deleted ${files.length} storage files`);
        }
      }
      
      // Also try to delete the folder itself (in case there are nested files)
      const { data: subfolders } = await supabase.storage
        .from('inventory-photos')
        .list(storagePath);
      
      if (subfolders) {
        for (const folder of subfolders) {
          if (folder.name !== '.emptyFolderPlaceholder') {
            const subPath = `${storagePath}/${folder.name}`;
            const { data: subFiles } = await supabase.storage
              .from('inventory-photos')
              .list(subPath);
            
            if (subFiles && subFiles.length > 0) {
              const subFilePaths = subFiles.map(f => `${subPath}/${f.name}`);
              await supabase.storage.from('inventory-photos').remove(subFilePaths);
            }
          }
        }
      }
    } catch (storageError) {
      console.warn(`[Delete Inventory] Storage cleanup failed (non-fatal):`, storageError);
      // Continue with database deletion even if storage cleanup fails
    }
    
    // Step 3: Delete database record
    const { error } = await supabase
      .from('inventories')
      .delete()
      .eq('id', id);

    if (error) throw error;

    console.log(`[Delete Inventory] ✅ Successfully deleted inventory ${id} and associated files`);
  } catch (error) {
    console.error('[Delete Inventory] Error deleting inventory:', error);
    throw error;
  }
};

// Retrofit Survey Operations
export const getAllRetrofitSurveysFromSupabase = async (): Promise<RetrofitSurveyListItem[]> => {
  try {
    // Get current user ID
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('[RetrofitSurveys] User not authenticated, returning empty list');
      return [];
    }
    
    console.log(`[RetrofitSurveys] 🔍 Current user ID: ${userId}`);
    
    // Add 2-second timeout for organization check
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        console.warn('[RetrofitSurveys] Organization check timed out, loading own files only');
        resolve(null);
      }, 2000);
    });
    
    // Check if user is in an organization and their role (use maybeSingle to avoid errors)
    const orgCheckPromise = supabase
      .from('organization_members')
      .select('organization_id, role')
      .eq('user_id', userId)
      .eq('is_active', true)
      .maybeSingle();
    
    const orgResult = await Promise.race([orgCheckPromise, timeoutPromise]);
    const memberData = orgResult && 'data' in orgResult ? orgResult.data : null;
    
    const isAdmin = memberData?.role === 'super_admin' || memberData?.role === 'admin';
    const organizationId = memberData?.organization_id;
    
    console.log(`[RetrofitSurveys] 👤 User role: ${memberData?.role || 'no organization'}`);
    console.log(`[RetrofitSurveys] 🏢 Organization ID: ${organizationId || 'none'}`);
    console.log(`[RetrofitSurveys] 🔑 Is Admin: ${isAdmin}`);
    
    // 🚀 OPTIMIZATION: Use PostgreSQL JSON operators to extract only address fields
    // instead of fetching the entire initial_questionnaire object
    let query = supabase
      .from('retrofit_surveys')
      .select(`
        id, 
        name, 
        created_at, 
        updated_at,
        user_id,
        organization_id,
        initial_questionnaire->houseNumberName,
        initial_questionnaire->streetAddress,
        initial_questionnaire->town,
        initial_questionnaire->postCode
      `);
    
    if (isAdmin && organizationId) {
      // Admins see ALL files from their organization
      console.log(`[RetrofitSurveys] 📂 Loading all files from organization (admin mode)`);
      query = query.eq('organization_id', organizationId);
    } else {
      // Regular members see ONLY their own files
      console.log(`[RetrofitSurveys] 📂 Loading only own files (member mode)`);
      query = query.eq('user_id', userId);
    }
    
    const { data, error } = await query.order('updated_at', { ascending: false });

    if (error) throw error;

    const ownFiles = data?.filter(f => f.user_id === userId).length || 0;
    const teamFiles = (data?.length || 0) - ownFiles;
    console.log(`[RetrofitSurveys] Loaded ${data?.length || 0} total files (${ownFiles} own, ${teamFiles} team)`);

    // Extract address from the individual fields
    const surveysWithAddress = (data || []).map(survey => {
      let address = '';
      try {
        // Access the extracted JSON fields directly
        const houseNumberName = (survey as any).houseNumberName;
        const streetAddress = (survey as any).streetAddress;
        const town = (survey as any).town;
        const postCode = (survey as any).postCode;
        
        const fullStreetAddress = houseNumberName && houseNumberName.trim()
          ? `${houseNumberName.trim()} ${streetAddress || ''}`
          : streetAddress || '';
        const parts = [fullStreetAddress, town, postCode].filter(part => part && part.trim());
        address = parts.join(', ');
      } catch (err) {
        console.warn(`Failed to extract address for survey ${survey.id}:`, err);
      }
      return {
        id: survey.id,
        name: survey.name,
        address,
        created_at: survey.created_at,
        updated_at: survey.updated_at
      };
    });

    console.log(`✅ Retrieved ${surveysWithAddress.length} retrofit surveys (optimized query - address fields only)`);
    return surveysWithAddress;
  } catch (error) {
    console.error('Error fetching retrofit surveys:', error);
    throw error;
  }
};

export const deleteRetrofitSurveyFromSupabase = async (id: string): Promise<void> => {
  try {
    // Step 1: Get user email for path construction
    const userEmail = await getCurrentUserEmail();
    if (!userEmail) {
      throw new Error('User not authenticated');
    }
    const sanitizedEmail = sanitizeEmail(userEmail);
    
    // Step 2: Get survey data to find property address for storage path
    // NOTE: Survey fields are stored as top-level columns, not in a nested 'survey_data' field
    const { data: surveyData } = await supabase
      .from('retrofit_surveys')
      .select('name, initial_questionnaire')
      .eq('id', id)
      .single();
    
    console.log(`[Delete Retrofit Survey] 🔍 Survey data structure:`, {
      hasName: !!surveyData?.name,
      hasInitialQuestionnaire: !!surveyData?.initial_questionnaire,
      name: surveyData?.name
    });
    
    // Extract property address from survey data using utility function
    let propertyPath = '';
    let propertyAddress = '';
    
    if (surveyData?.initial_questionnaire) {
      try {
        // Build a minimal RetrofitSurveyData object for the utility function
        const minimalSurveyData = {
          initial_questionnaire: surveyData.initial_questionnaire
        } as any;
        
        propertyAddress = getRetrofitSurveyAddress(minimalSurveyData);
        console.log(`[Delete Retrofit Survey] 📍 Extracted property address: "${propertyAddress}"`);
        
        // Sanitize property address for path
        if (propertyAddress) {
          propertyPath = propertyAddress
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .substring(0, 50);
          console.log(`[Delete Retrofit Survey] 📍 Sanitized path: "${propertyPath}"`);
        }
      } catch (error) {
        console.warn(`[Delete Retrofit Survey] ⚠️ Failed to extract property address:`, error);
      }
    }
    
    if (!propertyPath) {
      console.warn(`[Delete Retrofit Survey] ⚠️ No property address found in survey data, will try survey ID as fallback`);
    }
    
    // Fallback: try both property-based path and id-based path
    const pathsToTry = propertyPath 
      ? [`${sanitizedEmail}/${propertyPath}`, `${sanitizedEmail}/${id}`]
      : [`${sanitizedEmail}/${id}`];
    
    console.log(`[Delete Retrofit Survey] 🔍 Checking storage paths:`, pathsToTry);
    
    // Step 3: Delete files from both retrofit survey buckets
    const bucketsToClean: Array<'retrofit-survey-images' | 'retrofit-survey-files'> = [
      'retrofit-survey-images',
      'retrofit-survey-files'
    ];
    
    // Helper function to recursively delete all files in a folder
    const recursivelyDeleteFolder = async (bucket: string, folderPath: string): Promise<number> => {
      let totalDeleted = 0;
      
      try {
        // List all items in this folder
        const { data: items, error: listError } = await supabase.storage
          .from(bucket)
          .list(folderPath, {
            limit: 1000,
            offset: 0,
          });
        
        if (listError || !items || items.length === 0) {
          return totalDeleted;
        }
        
        // Separate files and folders
        const files = items.filter(item => item.id); // Files have IDs
        const folders = items.filter(item => !item.id && item.name !== '.emptyFolderPlaceholder'); // Folders don't have IDs
        
        // Delete all files in this folder
        if (files.length > 0) {
          const filePaths = files.map(file => `${folderPath}/${file.name}`);
          const { error: deleteError } = await supabase.storage
            .from(bucket)
            .remove(filePaths);
          
          if (!deleteError) {
            totalDeleted += files.length;
            console.log(`[Delete Retrofit Survey] ✅ Deleted ${files.length} files from ${bucket}/${folderPath}`);
          } else {
            console.warn(`[Delete Retrofit Survey] ⚠️ Error deleting files from ${bucket}/${folderPath}:`, deleteError);
          }
        }
        
        // Recursively delete all subfolders
        for (const folder of folders) {
          const subFolderPath = `${folderPath}/${folder.name}`;
          const subDeleted = await recursivelyDeleteFolder(bucket, subFolderPath);
          totalDeleted += subDeleted;
        }
        
        return totalDeleted;
      } catch (error) {
        console.warn(`[Delete Retrofit Survey] ⚠️ Error in recursive delete for ${bucket}/${folderPath}:`, error);
        return totalDeleted;
      }
    };
    
    // Delete files from all buckets
    for (const bucket of bucketsToClean) {
      console.log(`[Delete Retrofit Survey] 🧹 Cleaning bucket: ${bucket}`);
      
      for (const storagePath of pathsToTry) {
        try {
          const deletedCount = await recursivelyDeleteFolder(bucket, storagePath);
          if (deletedCount > 0) {
            console.log(`[Delete Retrofit Survey] ✅ Total deleted from ${bucket}/${storagePath}: ${deletedCount} files`);
          }
        } catch (storageError) {
          console.warn(`[Delete Retrofit Survey] ⚠️ Storage cleanup failed for ${bucket}/${storagePath} (non-fatal):`, storageError);
        }
      }
    }
    
    // Step 4: Delete database record
    const { error } = await supabase
      .from('retrofit_surveys')
      .delete()
      .eq('id', id);

    if (error) throw error;

    console.log(`[Delete Retrofit Survey] ✅ Successfully deleted retrofit survey ${id} and associated files`);
  } catch (error) {
    console.error('[Delete Retrofit Survey] Error deleting retrofit survey:', error);
    throw error;
  }
};

// Storage operations for photos (using Supabase Storage)
export const uploadPhotoToSupabase = async (
  photo: string,
  fileName: string,
  bucket: 'inventory-photos' | 'floorplan-images' | 'retrofit-survey-images' | 'retrofit-survey-files' = 'retrofit-survey-images'
): Promise<string> => {
  try {
    // Get current user email for user-specific folder
    const userEmail = await getCurrentUserEmail();
    if (!userEmail) {
      throw new Error('User not authenticated');
    }
    
    // Create email-based path: sanitized-email/filename
    const sanitizedEmail = sanitizeEmail(userEmail);
    const userSpecificPath = `${sanitizedEmail}/${fileName}`;
    
    // Convert base64 to blob
    const base64Data = photo.replace(/^data:image\/[a-z]+;base64,/, '');
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/jpeg' });

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(userSpecificPath, blob, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    // Get public URL
    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(userSpecificPath);

    return publicUrlData.publicUrl;
  } catch (error) {
    console.error('Error uploading photo to Supabase Storage:', error);
    throw error;
  }
};

export const deletePhotoFromSupabase = async (
  fileName: string,
  bucket: 'inventory-photos' | 'floorplan-images' | 'retrofit-survey-images' | 'retrofit-survey-files' = 'retrofit-survey-images'
): Promise<void> => {
  try {
    // Get current user email for email-based paths
    const userEmail = await getCurrentUserEmail();
    if (!userEmail) {
      throw new Error('User not authenticated');
    }
    const sanitizedEmail = sanitizeEmail(userEmail);
    
    // Also get user ID for backward compatibility with old paths
    const userId = await getCurrentUserId();
    
    // Handle both old paths (user_id/...) and new paths (email/...)
    // If fileName already contains '/', it's likely a full path - try both email and userId prefixes
    let pathsToTry: string[] = [];
    
    if (fileName.includes('/')) {
      // Full path provided - use as-is, and also try with email/userId prefixes
      pathsToTry = [
        fileName,  // Use as provided
        `${sanitizedEmail}/${fileName}`,  // Email-based path
        userId ? `${userId}/${fileName}` : '',  // Old user ID path (backward compat)
      ].filter(Boolean);
    } else {
      // Just filename - prepend email (new) and userId (old) for backward compat
      pathsToTry = [
        `${sanitizedEmail}/${fileName}`,
        userId ? `${userId}/${fileName}` : '',
      ].filter(Boolean);
    }
    
    console.log(`[Delete Photo] Attempting to delete from ${bucket}:`, pathsToTry);
    
    // Try each path until one succeeds
    let deleted = false;
    for (const pathToDelete of pathsToTry) {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([pathToDelete]);

      if (!error) {
        console.log(`[Delete Photo] ✅ Successfully deleted: ${pathToDelete}`);
        deleted = true;
        break;
      }
    }
    
    if (!deleted) {
      console.warn(`[Delete Photo] ⚠️ Could not delete ${fileName} from ${bucket} (may not exist)`);
    }
  } catch (error) {
    console.error('Error deleting photo from Supabase:', error);
    throw error;
  }
};

/**
 * Get photo metadata including file size from Supabase Storage
 * @param fileName - Full path to the file in storage
 * @param bucket - Storage bucket name
 * @returns File metadata including size in bytes, created_at, updated_at
 */
export const getPhotoMetadata = async (
  fileName: string,
  bucket: 'inventory-photos' | 'floorplan-images' | 'retrofit-survey-images' | 'retrofit-survey-files' = 'retrofit-survey-images'
): Promise<{
  name: string;
  size: number; // Size in bytes
  created_at: string;
  updated_at: string;
  last_accessed_at: string;
  metadata?: Record<string, any>;
} | null> => {
  try {
    // Extract directory path and filename
    const pathParts = fileName.split('/');
    const actualFileName = pathParts[pathParts.length - 1];
    const directory = pathParts.slice(0, -1).join('/');

    // List files in the directory to get metadata
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(directory || '', {
        search: actualFileName
      });

    if (error) {
      console.error('Error getting photo metadata:', error);
      throw error;
    }

    // Find the exact file match
    const fileMetadata = data?.find(file => file.name === actualFileName);
    
    if (!fileMetadata) {
      // Silent fail - file not found (likely wrong fileName)
      return null;
    }

    return {
      name: fileMetadata.name,
      size: fileMetadata.metadata?.size || 0,
      created_at: fileMetadata.created_at,
      updated_at: fileMetadata.updated_at,
      last_accessed_at: fileMetadata.last_accessed_at,
      metadata: fileMetadata.metadata
    };
  } catch (error) {
    console.error('Error getting photo metadata from Supabase:', error);
    throw error;
  }
};

// Test Supabase connection
export const testSupabaseConnection = async (): Promise<{ connected: boolean; error?: string }> => {
  try {
    // 🚀 OPTIMIZATION: Run both table tests in parallel instead of sequentially
    // Use count(*) instead of selecting actual rows for faster response
    console.log('[Supabase] Testing database connection (parallel tests)...');
    
    // Add 3-second timeout protection to prevent hanging
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Connection test timed out after 3 seconds')), 3000);
    });
    
    const queryPromise = Promise.all([
      supabase.from('floorplans').select('id', { count: 'exact', head: true }),
      supabase.from('inventories').select('id', { count: 'exact', head: true })
    ]);
    
    const [floorplansResult, inventoriesResult] = await Promise.race([
      queryPromise,
      timeoutPromise
    ]) as Awaited<typeof queryPromise>;

    // Check floorplans table
    if (floorplansResult.error) {
      const basicError = floorplansResult.error;
      console.error('[Supabase] Floorplans table test failed:', basicError);
      
      // PGRST002: Schema cache error - This is a temporary Supabase issue, not a connection failure
      if (basicError.code === 'PGRST002') {
        console.warn('[Supabase] Temporary schema cache error (PGRST002) - treating as connected');
        return { connected: true };
      }
      
      // Check if it's a table doesn't exist issue
      if (basicError.code === '42P01') {
        return { 
          connected: false, 
          error: 'Database tables not found. Please run the setup SQL script to create the required tables.' 
        };
      }
      
      // Check if it's a column missing issue
      if (basicError.code === '42703') {
        return { 
          connected: false, 
          error: `Database schema issue: ${basicError.message}. Please run the setup SQL script.` 
        };
      }
      
      return { connected: false, error: basicError.message };
    }

    // Check inventories table
    if (inventoriesResult.error) {
      const inventoryError = inventoriesResult.error;
      console.error('[Supabase] Inventories table test failed:', inventoryError);
      
      if (inventoryError.code === '42P01') {
        return { 
          connected: false, 
          error: 'Inventories table not found. Please run the setup SQL script to create the required tables.' 
        };
      }
      
      if (inventoryError.code === '42703') {
        return { 
          connected: false, 
          error: `Inventories table missing columns: ${inventoryError.message}. Please run the setup SQL script.` 
        };
      }
      
      return { connected: false, error: `Inventories table error: ${inventoryError.message}` };
    }

    console.log('[Supabase] ✅ Connection test successful (optimized parallel check)');
    return { connected: true };
  } catch (error) {
    console.error('[Supabase] Connection test error:', error);
    
    // If it's a network error or similar, provide helpful message
    if (error instanceof Error) {
      if (error.message.includes('fetch')) {
        return { 
          connected: false, 
          error: 'Cannot connect to Supabase. Check your internet connection and Supabase configuration.' 
        };
      }
    }
    
    return { 
      connected: false, 
      error: error instanceof Error ? error.message : 'Unknown connection error' 
    };
  }
};

/**
 * Upload HTML content to Supabase Storage for hosted viewing
 * @param htmlContent - HTML string to upload
 * @param fileName - Name for the file (e.g., 'property-inventory-checkOut-2026-01-09.html')
 * @returns Public URL where the HTML can be accessed
 */
export const uploadHTMLToSupabase = async (
  htmlContent: string,
  fileName: string
): Promise<string> => {
  try {
    console.log(`📤 Uploading HTML to Supabase: ${fileName}`);
    
    // Upload HTML to storage
    const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
    
    const { data, error } = await supabase.storage
      .from('inventory-exports')
      .upload(fileName, blob, {
        cacheControl: '3600',
        upsert: true,
        contentType: 'text/html'
      });

    if (error) {
      console.error('Error uploading HTML:', error);
      throw error;
    }

    console.log('✅ HTML uploaded successfully:', data.path);

    // Return Edge Function URL that serves the HTML with proper content-type
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://qazhweunmfzifeenbozk.supabase.co';
    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/render-html?file=${fileName}`;

    console.log('🌐 Edge Function URL:', edgeFunctionUrl);

    return edgeFunctionUrl;
  } catch (error) {
    console.error('❌ Failed to upload HTML to Supabase:', error);
    throw error;
  }
};

/**
 * Delete all photos for a retrofit survey from Supabase Storage
 * This deletes the entire folder structure for a survey: {email}/{property-address}/
 */
export const deleteAllSurveyPhotos = async (propertyAddress: string): Promise<void> => {
  try {
    const userEmail = await getCurrentUserEmail();
    if (!userEmail) {
      console.warn('[Storage] No user email found, skipping photo deletion');
      return;
    }
    
    const sanitizedEmail = sanitizeEmail(userEmail);
    const sanitizedAddress = propertyAddress
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const folderPrefix = `${sanitizedEmail}/${sanitizedAddress}`;
    
    console.log(`[Storage] 🗑️ Deleting all photos for survey: ${folderPrefix}`);
    
    // List all files in the survey folder
    const { data: files, error: listError } = await supabase.storage
      .from('retrofit-survey-images')
      .list(folderPrefix, {
        limit: 1000, // Adjust if you have more than 1000 photos per survey
        sortBy: { column: 'name', order: 'asc' }
      });
    
    if (listError) {
      console.error('[Storage] ❌ Error listing survey photos:', listError);
      throw listError;
    }
    
    if (!files || files.length === 0) {
      console.log('[Storage] ℹ️ No photos found for survey');
      return;
    }
    
    console.log(`[Storage] 📸 Found ${files.length} files to delete`);
    
    // Build full paths for deletion
    const filePaths = files.map(file => `${folderPrefix}/${file.name}`);
    
    // Delete all files in batch
    const { data: deleteData, error: deleteError } = await supabase.storage
      .from('retrofit-survey-images')
      .remove(filePaths);
    
    if (deleteError) {
      console.error('[Storage] ❌ Error deleting survey photos:', deleteError);
      throw deleteError;
    }
    
    console.log(`[Storage] ✅ Successfully deleted ${filePaths.length} photos from survey`);
    
    // Try to list subdirectories and delete their contents too
    // (in case there are nested folders like {section}/{photoType}/)
    try {
      const { data: folders, error: folderListError } = await supabase.storage
        .from('retrofit-survey-images')
        .list(sanitizedEmail, {
          limit: 100,
          search: sanitizedAddress
        });
      
      if (!folderListError && folders && folders.length > 0) {
        for (const folder of folders) {
          if (folder.name.includes(sanitizedAddress)) {
            const subFolderPath = `${sanitizedEmail}/${folder.name}`;
            const { data: subFiles } = await supabase.storage
              .from('retrofit-survey-images')
              .list(subFolderPath, { limit: 1000 });
            
            if (subFiles && subFiles.length > 0) {
              const subFilePaths = subFiles.map(f => `${subFolderPath}/${f.name}`);
              await supabase.storage
                .from('retrofit-survey-images')
                .remove(subFilePaths);
              
              console.log(`[Storage] ✅ Deleted ${subFilePaths.length} files from subfolder ${folder.name}`);
            }
          }
        }
      }
    } catch (subError) {
      console.warn('[Storage] ⚠️ Error cleaning up subdirectories:', subError);
      // Don't fail the whole operation if subfolder cleanup fails
    }
    
  } catch (error) {
    console.error('[Storage] ❌ Failed to delete survey photos:', error);
    throw error;
  }
}; 